import {
  TextField as MaterialUiTextField,
  TextFieldProps as MaterialUiTextFieldProps
} from '@mui/material'
import { DatePickerProps, DesktopDatePicker, MobileDatePicker } from '@mui/lab'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import enLocale from 'date-fns/locale/en-US'
import fiLocale from 'date-fns/locale/fi'
import svLocale from 'date-fns/locale/sv'
import { useEffect, useState } from 'react'
import {
  CommonFieldProps,
  CommonInputProps,
  FieldWrapper,
  FieldWrapperProps
} from '.'
import { useIsMobile, useTranslationData } from '../contexts'
import {
  useFieldLabelAndPlaceholder,
  useFieldProps,
  useTranslation
} from '../hooks'
import {
  BASIC_DATE_FORMAT,
  formatISODateMidnight,
  isDateValid,
  parseISODate
} from '../utils'

const localeMap = {
  en: enLocale,
  fi: fiLocale,
  sv: svLocale
}

type DatePickerTextFieldProps = Pick<
  MaterialUiTextFieldProps,
  | 'error'
  | 'helperText'
  | 'InputProps'
  | 'inputRef'
  | 'onBlur'
  | 'onChange'
  | 'value'
> &
  CommonInputProps &
  Pick<CommonFieldProps, 'name'>

const DatePickerTextField = ({
  name,
  margin,
  variant,
  ...rest
}: DatePickerTextFieldProps) => {
  return (
    <MaterialUiTextField
      {...rest}
      id={name}
      margin={margin || 'none'}
      variant={variant || 'standard'}
    />
  )
}

const parseMUIDate = (muiDate: unknown) => parseISODate(muiDate as string)

const getInvalidDateStrOrNull = (date: Date) => {
  // string '∞' is invalid as a date which shows yup's typeError message
  return date ? '∞' : null
}

export type DatePickerWrapperProps = Pick<
  DatePickerProps,
  'disabled' | 'maxDate' | 'minDate'
> &
  Omit<
    DatePickerTextFieldProps,
    'error' | 'helperText' | 'InputProps' | 'name'
  > &
  CommonFieldProps

export const DatePickerWrapper = ({
  disabled,
  fontSize,
  label,
  labelTranslationId,
  maxDate,
  minDate,
  name,
  executeOnChangeFn,
  placeholder,
  placeholderTranslationId,
  ...textFieldProps
}: DatePickerWrapperProps) => {
  const { error, helperText, onBlur, setValue, value } = useFieldProps(name)

  const [selectedDate, setSelectedDate] = useState<Date | null>(
    parseMUIDate(value)
  )

  useEffect(() => {
    if (value !== undefined) {
      setSelectedDate(parseMUIDate(value))
    } else {
      setSelectedDate(null)
    }
  }, [value])

  const isMobile = useIsMobile()

  const { UILanguage } = useTranslationData()

  const cancelLabel = useTranslation('common.cancel')

  const { fieldLabel, fieldPlaceholder } = useFieldLabelAndPlaceholder(
    labelTranslationId,
    label,
    placeholderTranslationId,
    placeholder
  )

  const handleDateChange = (muiDate: unknown) => {
    const date = muiDate as Date
    setSelectedDate(date)
    // string '∞' is invalid as a date which shows yup's typeError message
    const day = isDateValid(date)
      ? formatISODateMidnight(date)
      : getInvalidDateStrOrNull(date)
    setValue(day, true)
    if (executeOnChangeFn) {
      executeOnChangeFn()
    }
  }

  const datePickerProps: DatePickerProps = {
    cancelText: cancelLabel,
    inputFormat: BASIC_DATE_FORMAT,
    disabled,
    label: fieldLabel,
    mask: '__.__.____',
    maxDate: parseMUIDate(maxDate),
    minDate: parseMUIDate(minDate),
    onChange: handleDateChange,
    renderInput: (params) => (
      <DatePickerTextField
        {...textFieldProps}
        {...params}
        error={!!error}
        InputProps={{
          ...params.InputProps,
          sx: { fontSize }
        }}
        helperText={helperText}
        name={name}
        onBlur={onBlur}
        placeholder={fieldPlaceholder}
      />
    ),
    value: selectedDate
  }

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      locale={localeMap[UILanguage]}
    >
      {isMobile ? (
        <MobileDatePicker {...datePickerProps} />
      ) : (
        <DesktopDatePicker {...datePickerProps} />
      )}
    </LocalizationProvider>
  )
}

export type FormikDateFieldProps = Partial<FieldWrapperProps> &
  DatePickerWrapperProps

export const FormikDateField = ({
  hidefield = false,
  gridProps,
  ...datePickerProps
}: FormikDateFieldProps) => {
  return (
    <FieldWrapper hidefield={hidefield} gridProps={{ md: gridProps?.md || 2 }}>
      <DatePickerWrapper {...datePickerProps} />
    </FieldWrapper>
  )
}
